import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProductFilterFacet } from "@/shared/components/ui/product-filter-menu";
import api from "@/admin/lib/api";
import { invalidateWorkflowInstanceQueries } from "@/shared/hooks/use-workflows";
import type { ClaimRow } from "./use-claims";

// ── Choices (platform-level enums from backend) ──────────

export interface ChoiceOption {
  value: string;
  label: string;
}

export interface InspectionChoices {
  lifecycle_statuses: ChoiceOption[];
  results: ChoiceOption[];
  evidence_kinds: ChoiceOption[];
  trigger_sources: ChoiceOption[];
  note_kinds: ChoiceOption[];
  note_visibilities: ChoiceOption[];
  punch_list_statuses: ChoiceOption[];
  /** Field-inspector classification (QA / Claims / Both) from the backend. */
  inspector_types: ChoiceOption[];
}

export function useInspectionChoices() {
  return useQuery({
    queryKey: ["inspection-choices"],
    queryFn: async () => {
      const res = await api.get<InspectionChoices>("/inspections/choices/");
      return res.data;
    },
    staleTime: 1000 * 60 * 30,
  });
}

// ── Types ────────────────────────────────────────────────

export interface InspectionRow {
  uuid: string;
  code: string;
  claim_uuid: string | null;
  /** The claim's human reference, e.g. `CLM-14F63858`. Null when unlinked. */
  claim_ref: string | null;
  /**
   * Priority and SLA the originating claim works to, from its workflow's
   * metadata. Null when the workflow declares none.
   */
  claim_service_level: { is_priority: boolean; sla_days: number | null } | null;
  /** Claim facts an inspector needs before a visit: type, defect, product. */
  claim_context: {
    claim_type?: string | null;
    /** Short form: "Job Stop", "Installed". */
    claim_type_label?: string | null;
    property_address?: Record<string, string> | null;
    defect?: string | null;
    defect_detail?: string | null;
    product_impacted?: string | null;
    description?: string | null;
  } | null;
  sub_type_key: string;
  sub_type_label: string;
  lifecycle_status: string;
  result: string;
  result_reason: string;
  trigger_source: string;
  /**
   * Explicit "the team says this one is urgent" flag. Independent of
   * `trigger_source` (where the work came from) - render them as two
   * attributes, never as one collapsed badge.
   */
  is_priority: boolean;
  priority_reason: string;
  scheduled_for: string | null;
  started_at: string | null;
  completed_at: string | null;
  inspector_email: string | null;
  inspector_name: string;
  contractor_name: string | null;
  property_address_line_1: string;
  property_city: string;
  property_state: string;
  property_postal_code: string;
  repair_required: boolean;
  repair_completed_at: string | null;
  evidence_count: number;
  spotlight?: {
    active: boolean;
    is_new: boolean;
    title: string;
    level: "info" | "attention" | "critical";
  };
  created_at: string;
  updated_at: string;
  /**
   * Smart Schedule trip chip (scheduling-console feed only): the live
   * itinerary this visit belongs to, or null/absent when unrouted.
   */
  itinerary?: { uuid: string; name: string } | null;
}

export interface InspectionSubType {
  uuid: string;
  key: string;
  label: string;
  description: string;
  icon: string;
  sort_order: number;
  trigger_source: string;
  requires_claim: boolean;
  requires_holding: boolean;
  attribute_codes_required: string[];
  attribute_codes_optional: string[];
  default_letter_templates: Record<string, string>;
  workflow_key: string;
  is_active: boolean;
}

// ── Queries ─────────────────────────────────────────────

export interface InspectionListParams {
  /** Filter by linked claim UUID. */
  claim?: string;
  sub_type?: string;
  status?: string;
  /** Inspector uuid, or the sentinel `'none'` for unassigned. */
  inspector?: string;
  /** `'none'` → only unscheduled (scheduled_for is null). */
  scheduled?: string;
  scheduled_from?: string;
  scheduled_to?: string;
  /** `'true'` → only the staged queue (draft + unscheduled). */
  staged?: string;
  /** `'true'` → exclude drafts already on a live Smart Schedule itinerary. */
  unrouted?: string;
  /**
   * Landing split: `'priority'` = ad-hoc work (claim-triggered, manual,
   * API…) most urgent first; `'pool'` = the bulk registration programme.
   */
  bucket?: string;
}

/**
 * List inspections for the current organisation. Returns up to 200 rows
 * sorted by scheduled_for desc; pagination is added in a follow-up PR.
 */
export function useInspections(params: InspectionListParams = {}) {
  const search = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== ""),
  ).toString();
  const qs = search ? `?${search}` : "";
  return useQuery({
    queryKey: ["inspections", params],
    queryFn: async () => {
      // axios interceptor in admin/lib/api.ts auto-unwraps the
      // {status, message, result} envelope, so res.data is the array directly.
      const res = await api.get<InspectionRow[]>(`/inspections/${qs}`);
      return res.data ?? [];
    },
  });
}

/** One page of the inspections list, with its status counts and facets. */
export interface InspectionsListPage {
  count: number;
  results: InspectionRow[];
  /** Inspections per lifecycle status, counted before the status filter. */
  status_counts?: Record<string, number>;
  /** The data-driven Filters panel facets (inspectors, states). */
  filter_options?: ProductFilterFacet[];
}

/** The inspections list, one server page at a time (every filter server-side). */
export function useInspectionsPage(params: Record<string, string>) {
  return useQuery({
    queryKey: ["inspections", "page", params],
    queryFn: async () =>
      (await api.get<InspectionsListPage>("/inspections/", { params })).data,
    // The last page stays while the next loads, so the Filters panel's
    // facets (which ride on the page) do not blank mid-pick.
    placeholderData: keepPreviousData,
  });
}

// ── Scheduling Console feed ──────────────────────────────────

export interface SchedulingConsoleParams {
  /** ISO date string YYYY-MM-DD; defaults to today server-side. */
  from?: string;
  /** ISO date string YYYY-MM-DD; defaults to today + 30 days server-side. */
  to?: string;
  /** Optional lifecycle filter. Default excludes cancelled + closed. */
  status?: string;
  /** Optional sub-type-key filter. */
  sub_type?: string;
  /** Optional inspector-uuid filter. */
  inspector?: string;
}

/**
 * Scheduling Console feed - inspections in a date window, sorted ASC by
 * `scheduled_for`. Excludes cancelled + closed by default (override via
 * `status=cancelled` etc.). Used by the Calendar view at
 * /inspections/scheduling. Different from `useInspections` which is the
 * paginated list view sorted DESC and limited to 200.
 */
export function useSchedulingInspections(params: SchedulingConsoleParams = {}) {
  const search = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== ""),
  ).toString();
  const qs = search ? `?${search}` : "";
  return useQuery({
    queryKey: ["inspections-scheduling", params],
    queryFn: async () => {
      const res = await api.get<InspectionRow[]>(
        `/inspections/scheduling/${qs}`,
      );
      return res.data ?? [];
    },
  });
}

// ── Geocode inspection addresses for the Scheduling Console Map ──
export interface GeocodedPoint {
  lat: number;
  lng: number;
  formatted_address: string;
}

export interface GeocodeResult {
  resolved: Record<string, GeocodedPoint>;
  unresolved: string[];
}

export function useGeocodeInspections() {
  return useMutation({
    mutationFn: async (inspectionUuids: string[]) => {
      const res = await api.post<GeocodeResult>(
        "/inspections/scheduling/geocode/",
        { inspection_uuids: inspectionUuids },
      );
      return res.data ?? { resolved: {}, unresolved: [] };
    },
  });
}

// ── Inspector search (typeahead source for wizard) ──────────

export interface InspectorOption {
  uuid: string;
  email: string;
  full_name: string;
  role: string;
  /** Member's inspector classification: 'qa' | 'claims' | 'both'. */
  inspector_type: string;
}

/**
 * Inspector typeahead. Pass `inspectorType` ('qa' | 'claims') to restrict
 * the candidates to members eligible for that kind of inspection (the
 * backend also includes dual-capable `both` members). Omit it (or pass
 * `''`/`'both'`) to search every member.
 */
export function useInspectorSearch(search: string, inspectorType = "") {
  return useQuery({
    queryKey: ["inspection-inspectors", search, inspectorType],
    enabled: search.trim().length >= 2,
    queryFn: async () => {
      const res = await api.get<InspectorOption[]>("/inspections/inspectors/", {
        params: {
          search,
          ...(inspectorType ? { inspector_type: inspectorType } : {}),
        },
      });
      return res.data ?? [];
    },
    staleTime: 30_000,
  });
}

/**
 * FTS who cover this inspection's GeoZone (Phase 3 - resolved
 * server-side from the snapshotted postal/state, no geocoding). Same
 * shape as `useInspectorSearch` so the assign picker can pre-seed the
 * recommendation. Empty when the zone can't be resolved / nobody
 * covers it - never an error.
 */
export function useSuggestedInspectors(uuid: string | undefined) {
  return useQuery({
    queryKey: ["inspection-suggested-inspectors", uuid],
    enabled: !!uuid,
    queryFn: async () => {
      const res = await api.get<InspectorOption[]>(
        `/inspections/${uuid}/suggested-inspectors/`,
      );
      return res.data ?? [];
    },
    staleTime: 60_000,
  });
}

// ── Holding search (typeahead source for wizard) ────────────

/**
 * Shape returned by `/registrations/` list endpoint
 * (`HoldingListSerializer` at backend/apps/products/serializers.py).
 * Only the fields the picker reads are typed here; the full row has
 * more.
 */
export interface HoldingSearchOption {
  uuid: string;
  registration_number?: string | null;
  product?: { uuid?: string; cached_short_name?: string | null } | null;
  consumer_account?: {
    uuid?: string;
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  host?: {
    uuid?: string;
    name?: string | null;
    address?: { display?: string | null } | null;
    address_line_1?: string | null;
    address_town_or_city?: string | null;
    address_postcode?: string | null;
  } | null;
  warranties?: { class_name?: string | null }[] | null;
  /**
   * The dealer who registered this holding. Carries the downstream org's own
   * uuid so a caller can act on the installing contractor (the inspection
   * wizard pre-fills its contractor picker from it) without re-searching by
   * name.
   */
  partnership?: {
    uuid?: string;
    downstream_organisation_uuid?: string | null;
    downstream_organisation_name?: string | null;
    membership_number?: string | null;
  } | null;
}

/** Build a `First Last` display name from a consumer_account, or `''`. */
export function holdingConsumerName(h: HoldingSearchOption): string {
  const c = h.consumer_account;
  if (!c) return "";
  const parts = [c.first_name, c.last_name].filter(Boolean);
  return parts.length ? parts.join(" ") : c.email || "";
}

/** Build a `Town, Postcode` location line for a holding's host, or `''`. */
export function holdingLocation(h: HoldingSearchOption): string {
  const host = h.host;
  if (!host) return "";
  // The list/search endpoint returns the formatted address on
  // `host.address.display` (and `host.name`); the flat address_* fields
  // are a legacy fallback.
  const display = host.address?.display || host.name;
  if (display) return display;
  const parts = [
    host.address_line_1,
    host.address_town_or_city,
    host.address_postcode,
  ]
    .filter(Boolean)
    .map((x) => x as string);
  return parts.join(", ");
}

export function useHoldingSearch(search: string) {
  return useQuery({
    queryKey: ["inspection-holding-search", search],
    enabled: search.trim().length >= 2,
    queryFn: async () => {
      // Reuses the existing /registrations/ list endpoint with a search
      // param. The backend already searches across consumer name/email,
      // product name, registration number, and host address.
      const res = await api.get<
        { results?: HoldingSearchOption[] } | HoldingSearchOption[]
      >("/registrations/", { params: { search, page_size: "20" } });
      const data = res.data;
      if (Array.isArray(data)) return data;
      return data?.results ?? [];
    },
    staleTime: 30_000,
  });
}

// ── Claim search (typeahead source for the wizard claim picker) ──

export function useClaimSearch(search: string) {
  return useQuery({
    queryKey: ["inspection-claim-search", search],
    enabled: search.trim().length >= 2,
    queryFn: async () => {
      // Reuses the /claims/claims/ list endpoint's DRF search (consumer
      // name/email/phone, product name, registration number, host name).
      const res = await api.get<{ results?: ClaimRow[] } | ClaimRow[]>(
        "/claims/claims/",
        { params: { search, page_size: "20" } },
      );
      const data = res.data;
      if (Array.isArray(data)) return data;
      return data?.results ?? [];
    },
    staleTime: 30_000,
  });
}

// ── Dashboard feed ──────────────────────────────────────────

export interface DashboardAgingBuckets {
  "0_30_days": number;
  "31_60_days": number;
  "61_90_days": number;
  over_90_days: number;
}

export interface DashboardTopContractor {
  contractor_uuid: string | null;
  contractor_name: string;
  open_count: number;
}

export interface DashboardRepairTracking {
  total_open_items: number;
  total_resolved_items: number;
  aging_buckets: DashboardAgingBuckets;
  top_contractors: DashboardTopContractor[];
  open_inspections: InspectionRow[];
}

export interface DashboardPayload {
  repair_tracking: DashboardRepairTracking;
}

export function useInspectionsDashboard() {
  return useQuery({
    queryKey: ["inspections-dashboard"],
    queryFn: async () => {
      const res = await api.get<DashboardPayload>("/inspections/dashboard/");
      return res.data;
    },
  });
}

// ── Dashboard V2 — Amanda's spec tiles ───────────────────

/**
 * Compact row shape returned by `/api/inspections/dashboard/v2/`.
 * Carries every column Amanda's spec calls for across the 9 tiles.
 */
export interface DashboardV2Row {
  uuid: string;
  code: string;
  sub_type_label: string;
  lifecycle_status: string;
  result: string;
  scheduled_for: string | null;
  completed_at: string | null;
  appointment_confirmed: boolean;
  contractor_name: string;
  inspector_email: string | null;
  inspector_name: string;
  assigned_to_email: string | null;
  assigned_to_name: string;
  property_address_line_1: string;
  property_city: string;
  property_state: string;
  homeowner_name: string;
  square_count: number | string | null;
}

export interface DashboardV2Payload {
  window_days: number;
  my_inspections: DashboardV2Row[];
  pjw_not_closed: DashboardV2Row[];
  inspections_scheduled: DashboardV2Row[];
  awaiting_follow_up: DashboardV2Row[];
  ytd_completed: DashboardV2Row[];
  counts: {
    my_inspections: number;
    pjw_not_closed: number;
    inspections_scheduled: number;
    awaiting_follow_up: number;
    ytd_completed: number;
  };
}

export function useInspectionsDashboardV2(windowDays: number = 7) {
  return useQuery({
    queryKey: ["inspections-dashboard-v2", windowDays],
    queryFn: async () => {
      const res = await api.get<DashboardV2Payload>(
        `/inspections/dashboard/v2/?window_days=${windowDays}`,
      );
      return res.data;
    },
  });
}

/** Sub-type catalogue for the current organisation (active only). */
export function useInspectionSubTypes() {
  return useQuery({
    queryKey: ["inspection-subtypes"],
    queryFn: async () => {
      const res = await api.get<InspectionSubType[]>("/inspections/subtypes/");
      return res.data ?? [];
    },
  });
}

// ── Single-record query ─────────────────────────────────────────

export interface InspectionEvidence {
  uuid: string;
  kind: string;
  caption: string;
  captured_at: string | null;
  gps_lat: number | null;
  gps_lng: number | null;
  // Shape of the shared UserUploadedFileSerializer (apps/files) -
  // fields are `file_url` / `file_name`, NOT `url` / `filename`.
  file: {
    uuid: string;
    file_url: string | null;
    file_name: string;
    thumbnail_url?: string | null;
  } | null;
  uploaded_by_email: string | null;
  approved_at: string | null;
  approved_by_email: string | null;
  /**
   * Phase 14 - line-item tagging per Release 1 S13-F006: optional list
   * of checklist criterion ids this photo evidences (e.g.
   * "drip_edge", "ventilation", "components", or safety-item
   * numbers from the Risk Assessment form). Used by the Evidence tab
   * to group photos by what they evidence.
   */
  tagged_line_items: string[];
  created_at: string;
}

export interface InspectionDetail {
  uuid: string;
  code: string;
  sub_type: InspectionSubType;
  trigger_source: string;
  trigger_notes: string;
  is_priority: boolean;
  priority_reason: string;
  lifecycle_status: string;
  result: string;
  result_reason: string;
  inspector_notes: string;
  claim_ref: string | null;
  /** Claim facts an inspector needs before a visit: type, defect, address. */
  claim_context: {
    claim_type?: string | null;
    claim_type_label?: string | null;
    defect?: string | null;
    defect_detail?: string | null;
    product_impacted?: string | null;
    description?: string | null;
    property_address?: Record<string, string> | null;
  } | null;
  claim_service_level: { is_priority: boolean; sla_days: number | null } | null;
  claim_uuid: string | null;
  holding_uuid: string | null;
  product_name: string | null;
  warranty_uuid: string | null;
  contractor_name: string | null;
  inspector_uuid: string | null;
  inspector_email: string | null;
  inspector_name: string;
  property_address_line_1: string;
  property_address_line_2: string;
  property_city: string;
  property_state: string;
  property_postal_code: string;
  property_country: string;
  consumer_name: string;
  consumer_email: string;
  consumer_phone: string;
  notify_customer: boolean;
  notify_contractor: boolean;
  requested_at: string | null;
  scheduled_for: string | null;
  rescheduled_count: number;
  appointment_confirmed: boolean;
  appointment_confirmed_at: string | null;
  assigned_to_uuid: string | null;
  assigned_to_email: string | null;
  assigned_to_name: string;
  started_at: string | null;
  completed_at: string | null;
  repair_required: boolean;
  repair_completed_at: string | null;
  repair_cycle_days: number | null;
  cancelled_at: string | null;
  cancellation_reason_code: string;
  cancellation_notes: string;
  risk_assessment: Record<string, unknown>;
  risk_assessment_completed_at: string | null;
  risk_assessment_signed_by: string;
  attribute_values: Record<string, unknown>;
  evidence: InspectionEvidence[];
  workflow_instance_uuid: string | null;
  created_at: string;
  updated_at: string;
}

export function useInspection(uuid: string | undefined) {
  return useQuery({
    queryKey: ["inspection", uuid],
    enabled: !!uuid,
    queryFn: async () => {
      const res = await api.get<InspectionDetail>(`/inspections/${uuid}/`);
      return res.data;
    },
  });
}

/**
 * Evidence aggregated across every inspection linked to a claim, each row
 * carrying its parent inspection's uuid + code so the claim detail page can
 * group documents by inspection and link back. Read-only - documents are
 * managed on the inspection record itself.
 */
export interface ClaimInspectionEvidence extends InspectionEvidence {
  inspection_uuid: string;
  inspection_code: string;
}

export function useClaimInspectionEvidence(claimUuid: string | undefined) {
  return useQuery({
    queryKey: ["inspections-claim-evidence", claimUuid],
    enabled: !!claimUuid,
    queryFn: async () => {
      const res = await api.get<ClaimInspectionEvidence[]>(
        "/inspections/evidence/",
        { params: { claim: claimUuid } },
      );
      return res.data;
    },
  });
}

// ── Inspection report metadata ───────────────────────────────────

/**
 * Per-option severity for MULTI_SELECT report sections. Mirrors the
 * `configuration_data.options[*].severity` the backend serves. The report
 * tab renders a red/amber chip per finding option; `none` shows no chip.
 */
export type InspectionReportSeverity = "red" | "yellow" | "none";

export interface InspectionReportOption {
  value: string;
  label: string;
  severity: InspectionReportSeverity;
}

/**
 * One field of the digital inspection report, as authored on the backend
 * as workflow attribute definitions. Fields arrive pre-sorted by `order`.
 * NUMBER + LONG_TEXT carry no options; MULTI_SELECT carries a per-option
 * severity used to chip findings.
 */
export interface InspectionReportField {
  code_name: string;
  display_name: string;
  field_type: "NUMBER" | "MULTI_SELECT" | "LONG_TEXT";
  required: boolean;
  order: number;
  description: string;
  configuration_data: {
    group: string;
    options?: InspectionReportOption[];
  };
}

/**
 * Field definitions for the digital inspection report. Driven entirely by
 * backend metadata (no hardcoded sections/options) - the report tab builds
 * its form from this. Enabled once the RA is complete (caller gates the
 * query) so we don't fetch for inspections that can't yet file a report.
 */
export function useInspectionReportMetadata(
  uuid: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ["inspection-report-metadata", uuid],
    enabled: !!uuid && enabled,
    queryFn: async () => {
      const res = await api.get<{ fields: InspectionReportField[] }>(
        `/inspections/${uuid}/report-metadata/`,
      );
      return res.data?.fields ?? [];
    },
  });
}

// ── Mutations ───────────────────────────────────────────

export interface InspectionCreatePayload {
  sub_type_key: string;
  trigger_source?: string;
  trigger_notes?: string;
  claim_uuid?: string | null;
  holding_uuid?: string | null;
  warranty_uuid?: string | null;
  contractor_uuid?: string | null;
  inspector_uuid?: string | null;
  scheduled_for?: string | null;
  attribute_values?: Record<string, unknown>;
  // Inline target snapshot - used when no Holding is linked (e.g. PJW
  // New Qualifying). Ignored by the backend when a Holding IS linked.
  property_address_line_1?: string;
  property_address_line_2?: string;
  property_city?: string;
  property_state?: string;
  property_postal_code?: string;
  property_country?: string;
  consumer_name?: string;
  consumer_email?: string;
  consumer_phone?: string;
  notify_customer?: boolean;
  notify_contractor?: boolean;
}

export function useCreateInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: InspectionCreatePayload) => {
      const res = await api.post<InspectionDetail>("/inspections/", payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspections"] });
    },
  });
}

export function useUpdateInspection(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<InspectionDetail>) => {
      const res = await api.patch<InspectionDetail>(
        `/inspections/${uuid}/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection", uuid] });
      qc.invalidateQueries({ queryKey: ["inspections"] });
    },
  });
}

/**
 * Reschedule an inspection from the Scheduling Console by drag-and-drop.
 * Uuid is passed per-call (the dragged card varies), unlike
 * ``useUpdateInspection`` which binds a single record. ``scheduled_for``
 * is an out-of-workflow SAFE_FIELD on the backend ``update_inspection``
 * service - no lifecycle change, so this is a pure data patch.
 */
export function useRescheduleInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { uuid: string; scheduled_for: string }) => {
      const res = await api.patch<InspectionDetail>(
        `/inspections/${payload.uuid}/`,
        { scheduled_for: payload.scheduled_for },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspections-scheduling"] });
      qc.invalidateQueries({ queryKey: ["inspections"] });
    },
  });
}

/**
 * Assign an unassigned (claim-triggered) inspection to an FTS **and**
 * schedule it in one PATCH (Phase 1 drag-from-queue). ``inspector_uuid``
 * is resolved server-side by ``update_inspection`` (the FE never sees
 * User pks). Invalidates the queue + scheduling + list feeds so the
 * card leaves the rail and lands on the calendar.
 */
export function useAssignInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      uuid: string;
      inspector_uuid: string;
      scheduled_for: string;
    }) => {
      const res = await api.patch<InspectionDetail>(
        `/inspections/${payload.uuid}/`,
        {
          inspector_uuid: payload.inspector_uuid,
          scheduled_for: payload.scheduled_for,
        },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspections-scheduling"] });
      qc.invalidateQueries({ queryKey: ["inspections"] });
    },
  });
}

// ── Route optimization (S13-F005) ─────────────────────────────

export interface RouteOptimizationDay {
  day: number;
  ordered_inspection_uuids: string[];
}

export interface RouteOptimizationResult {
  /** Inspection uuids in the optimal visiting order. */
  ordered_inspection_uuids: string[];
  distance_meters: number;
  /** Google drive time only (back-compat). */
  duration_seconds: number;
  /** Drive time + configurable on-site time per inspection. */
  total_duration_seconds: number;
  /** Configurable on-site minutes per inspection used for the total. */
  per_job_minutes: number;
  stop_count: number;
  /** Uuids dropped because they have no usable property address. */
  skipped_uuids: string[];
  /** True when the input exceeded Google's ~20-stop cap and was
   * chunked + stitched (real trip volumes). */
  chunked: boolean;
  /** Day model: 'overnight' (continuous trip, lodge en route) is the
   * working default; 'home_return' (home-anchored daily loops) is
   * echoed but a documented follow-up - needs a per-FTS base. */
  trip_mode: "overnight" | "home_return";
  /** Trip segmented into days (1 bucket when day-split is off). */
  days: RouteOptimizationDay[];
}

/**
 * Ask the backend for the most efficient visiting order for a set of
 * inspections (Google Routes API, server-side - the maps key never
 * reaches the browser). Used by the Scheduling Console to optimise a
 * single FTS day. The endpoint returns 503 when the maps key is not
 * provisioned in this environment and 502 on a provider failure; the
 * caller surfaces those gracefully rather than blocking the console.
 */
export function useOptimizeRoute() {
  return useMutation({
    mutationFn: async (inspectionUuids: string[]) => {
      const res = await api.post<RouteOptimizationResult>(
        "/inspections/scheduling/optimize-route/",
        { inspection_uuids: inspectionUuids },
      );
      return res.data;
    },
  });
}

// ── Bulk multi-day scheduling (routing chunk) ─────────────────

export interface AutoScheduleDay {
  /** ISO date (a weekday). */
  date: string;
  inspection_uuids: string[];
}

export interface AutoScheduleResult {
  summary: { scheduled: number; skipped: number; days: number; total: number };
  days: AutoScheduleDay[];
  skipped: { uuid: string; reason: string }[];
  route: {
    distance_meters?: number;
    duration_seconds?: number;
    total_duration_seconds?: number;
    per_job_minutes?: number;
    stop_count?: number;
    chunked?: boolean;
    trip_mode?: string;
  };
}

export interface AutoSchedulePayload {
  /** Staged (draft) inspection uuids to schedule. */
  inspection_uuids: string[];
  /** The field tech to assign the whole trip to. */
  inspector_uuid: string;
  /** First weekday of the trip, YYYY-MM-DD. */
  start_date: string;
  /** Optional drive+job time budget per day (minutes). */
  max_day_minutes?: number;
  /** Optional on-site time per inspection (minutes). */
  per_job_minutes?: number;
  /** Optional trip start point for the optimiser. */
  origin_address?: string;
}

/**
 * Bulk-schedule a batch of staged inspections onto one field tech as an
 * optimised multi-day weekday trip (the routing chunk / MapPoint replacement).
 * The server runs route optimisation, day-splits by drive time, and drives the
 * workflow `schedule` transition for each. Returns 503 when the maps key is not
 * provisioned; the caller surfaces that gracefully.
 */
export function useAutoScheduleInspections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AutoSchedulePayload) => {
      const res = await api.post<AutoScheduleResult>(
        "/inspections/scheduling/auto-schedule/",
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspections-scheduling"] });
      qc.invalidateQueries({ queryKey: ["inspections"] });
    },
  });
}

export function useCancelInspection(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { reason_code: string; notes?: string }) => {
      const res = await api.post<InspectionDetail>(
        `/inspections/${uuid}/cancel/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection", uuid] });
      qc.invalidateQueries({ queryKey: ["inspections"] });
    },
  });
}

// ── Appointment confirmation + follow-up assignment ───────

/**
 * Mark this inspection's scheduled appointment as confirmed (drives the
 * "confirmed / unconfirmed" badge on the dashboard tiles + the RED
 * "within 7 days unconfirmed" treatment). Sends a `confirm=true|false`
 * flag - `false` unconfirms.
 */
/**
 * Mark / unmark a single inspection as priority.
 *
 * Gated server-side by `inspections.mark_priority` - a narrow codename a
 * Resolution Specialist holds without the coarse inspections.edit permission.
 */
export function useMarkInspectionPriority(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      is_priority: boolean;
      priority_reason?: string;
    }) => {
      const res = payload.is_priority
        ? await api.post<InspectionDetail>(`/inspections/${uuid}/priority/`, {
            priority_reason: payload.priority_reason ?? "",
          })
        : await api.delete<InspectionDetail>(`/inspections/${uuid}/priority/`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection", uuid] });
      qc.invalidateQueries({ queryKey: ["inspections"] });
      qc.invalidateQueries({ queryKey: ["inspections-pool"] });
    },
  });
}

/** Tick several pool rows and mark them all in one call. */
export function useBulkMarkInspectionPriority() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      inspection_uuids: string[];
      is_priority: boolean;
      priority_reason?: string;
    }) => {
      const res = await api.post<{
        updated_uuids: string[];
        updated: number;
        requested: number;
        is_priority: boolean;
      }>("/inspections/priority/bulk/", payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspections"] });
      qc.invalidateQueries({ queryKey: ["inspections-pool"] });
    },
  });
}

export function useConfirmAppointment(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { confirm: boolean }) => {
      const res = payload.confirm
        ? await api.post<InspectionDetail>(
            `/inspections/${uuid}/confirm-appointment/`,
          )
        : await api.delete<InspectionDetail>(
            `/inspections/${uuid}/confirm-appointment/`,
          );
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["inspection", uuid] });
      qc.invalidateQueries({ queryKey: ["inspections"] });
      qc.invalidateQueries({ queryKey: ["inspections-scheduling"] });
      // Confirming the appointment drives the engine (scheduled -> confirmed),
      // so the workflow-instance and visible-actions queries that back the
      // progress rail, status banner, and top-right action menu must refetch
      // too - otherwise those surfaces keep showing the stale `scheduled`
      // state (and a phantom "Confirm Appointment" action) while the
      // appointment panel already reads "Confirmed".
      invalidateWorkflowInstanceQueries(qc, data?.workflow_instance_uuid);
    },
  });
}

/**
 * Assign the user who needs to take the next action on this inspection
 * (distinct from inspector - drives the "Awaiting Follow Up" dashboard
 * tile). Pass an empty `assignee_uuid` to clear.
 */
export function useAssignFollowUp(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { assignee_uuid: string }) => {
      const res = await api.post<InspectionDetail>(
        `/inspections/${uuid}/assign-follow-up/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection", uuid] });
      qc.invalidateQueries({ queryKey: ["inspections"] });
    },
  });
}

/**
 * Assign the inspector / field tech who performs the work (distinct from the
 * follow-up assignee). Pass an empty `inspector_uuid` to clear. Backs the
 * inspector assignment pill on the detail header so a coordinator can
 * (re)assign without re-running the schedule dialog.
 */
export function useAssignInspector(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { inspector_uuid: string }) => {
      const res = await api.post<InspectionDetail>(
        `/inspections/${uuid}/assign-inspector/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection", uuid] });
      qc.invalidateQueries({ queryKey: ["inspections"] });
      qc.invalidateQueries({ queryKey: ["inspections-scheduling"] });
    },
  });
}

// ── Punch list ───────────────────────────────────────────

export interface PunchListItem {
  uuid: string;
  description: string;
  category: string;
  status: "open" | "resolved" | "waived";
  status_label: string;
  sort_order: number;
  resolved_at: string | null;
  resolved_by_email: string;
  resolution_notes: string;
  waived_at: string | null;
  waived_by_email: string;
  waiver_reason: string;
  created_at: string;
  updated_at: string;
}

export function useInspectionPunchList(uuid: string | undefined) {
  return useQuery({
    queryKey: ["inspection-punch-list", uuid],
    enabled: !!uuid,
    queryFn: async () => {
      const res = await api.get<PunchListItem[]>(
        `/inspections/${uuid}/punch-list/`,
      );
      return res.data ?? [];
    },
  });
}

export function useCreatePunchListItem(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      description: string;
      category?: string;
      sort_order?: number;
    }) => {
      const res = await api.post<PunchListItem>(
        `/inspections/${uuid}/punch-list/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection-punch-list", uuid] });
    },
  });
}

export function useResolvePunchListItem(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { itemUuid: string; notes?: string }) => {
      const res = await api.post<PunchListItem>(
        `/inspections/${uuid}/punch-list/${payload.itemUuid}/resolve/`,
        { notes: payload.notes ?? "" },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection-punch-list", uuid] });
    },
  });
}

// ── Punch-list work list (across every inspection) ──────────────

/**
 * One item as it appears on the cross-inspection work list: the item's own
 * fields plus the context it needs once it is no longer sitting under its
 * inspection - whose work it is, where, and how long it has been outstanding.
 */
export interface PunchListWorkItem extends PunchListItem {
  inspection_uuid: string;
  inspection_code: string;
  inspection_status: string;
  /** Organisation uuid of the contractor, the stable key the facet filters on. */
  contractor_uuid: string | null;
  contractor_name: string;
  property_address: string;
  age_days: number;
}

export interface PunchListWorkListParams {
  /** `'open'` (default server-side), a specific status, or `'all'`. */
  status?: string;
  /** Contractor organisation uuids, comma-separated. */
  contractor?: string;
  /** Categories, comma-separated. */
  category?: string;
  /** Age buckets, comma-separated: `0_30_days` | `31_60_days` | `61_90_days` | `over_90_days`. */
  age?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

/** One page of the punch-list work list, with its Filters panel facets. */
export interface PunchListWorkPage {
  count: number;
  results: PunchListWorkItem[];
  filter_options?: ProductFilterFacet[];
}

export function usePunchListWorkList(params: PunchListWorkListParams = {}) {
  // Default to `'open'` to match the backend's own default, so an omitted
  // status and an explicit `'open'` share one query key.
  const { status = "open", ...rest } = params;
  const query: Record<string, string> = { status };
  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined && value !== "") query[key] = String(value);
  }
  return useQuery({
    queryKey: ["inspection-punch-list-work", query],
    queryFn: async () =>
      (await api.get<PunchListWorkPage>("/inspections/punch-list/", { params: query }))
        .data,
    // The last page stays while the next loads, so the Filters panel's
    // facets (which ride on the page) do not blank mid-pick.
    placeholderData: keepPreviousData,
  });
}

/**
 * Close one item from the work list. The row carries both uuids, so this
 * reuses the existing per-inspection endpoints rather than adding a second
 * write path for the same transition.
 *
 * Waiving is the deliberate "this will not be remedied, and that is agreed"
 * exit - it exists because an open item blocks its claim from settling, and
 * without it the only way to unblock one is to mark work resolved that
 * nobody did. The backend requires a reason.
 */
export function useClosePunchListWorkItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      inspectionUuid: string;
      itemUuid: string;
      action: "resolve" | "waive";
      notes?: string;
      reason?: string;
    }) => {
      const body =
        payload.action === "resolve"
          ? { notes: payload.notes ?? "" }
          : { reason: payload.reason ?? "" };
      const res = await api.post<PunchListItem>(
        `/inspections/${payload.inspectionUuid}/punch-list/${payload.itemUuid}/${payload.action}/`,
        body,
      );
      return res.data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["inspection-punch-list-work"] });
      qc.invalidateQueries({
        queryKey: ["inspection-punch-list", variables.inspectionUuid],
      });
      // The dashboard's open-item count and aging buckets are derived from
      // the same rows, so they are stale the moment one closes.
      qc.invalidateQueries({ queryKey: ["inspections-dashboard"] });
    },
  });
}

export interface PunchListSendPreview {
  open_item_count: number;
  recipients_to: string[];
  recipients_cc: string[];
  can_send: boolean;
  blocked_reason: string;
}

export interface PunchListSendResult {
  sent: string[];
  suppressed: { email: string; reason: string }[];
  item_count: number;
  recipients_to: string[];
  recipients_cc: string[];
  template_slug: string;
  communication_log_ids: number[];
}

export function usePunchListSendPreview(
  uuid: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["inspection-punch-list-send-preview", uuid],
    enabled: !!uuid && enabled,
    queryFn: async () => {
      const res = await api.get<PunchListSendPreview>(
        `/inspections/${uuid}/send-punch-list/`,
      );
      return res.data;
    },
  });
}

export function useSendPunchList(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { message?: string } = {}) => {
      const res = await api.post<PunchListSendResult>(
        `/inspections/${uuid}/send-punch-list/`,
        { message: payload.message ?? "" },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection-punch-list", uuid] });
      qc.invalidateQueries({
        queryKey: ["inspection-punch-list-send-preview", uuid],
      });
    },
  });
}

// ── Contractor quota (Inspection Matrix) ─────────────────

/**
 * Per-contractor inspection-matrix progress. Returned by the
 * ``/contractor-quotas/?org_uuids=...`` batched endpoint. The badge
 * surfaces on the wizard contractor picker so the Coordinator can see
 * at a glance who is behind on their PJW sample quota.
 */
export interface ContractorQuota {
  contractor_uuid: string;
  required_inspections: number;
  warranties_in_year: number;
  completed_in_year: number;
  inspections_remaining: number;
  behind_schedule: boolean;
  tier_label: string;
  year: number;
  /** Draft, unscheduled inspections already staged for this contractor. */
  staged_count: number;
}

export function useContractorQuotas(orgUuids: string[]) {
  // Sort the list so the cache key is stable regardless of result
  // ordering - the typeahead's order can churn between keystrokes.
  const sorted = [...orgUuids].sort();
  const key = sorted.join(",");
  return useQuery({
    queryKey: ["inspection-contractor-quotas", key],
    enabled: sorted.length > 0,
    queryFn: async () => {
      // POST the list in the body, not the query string: the pool console
      // can select hundreds of contractors, and that many UUIDs in a GET URL
      // exceeds the edge proxy's URL-length limit (a 400 before the request
      // reaches the backend). The endpoint still accepts GET for small lists.
      const res = await api.post<ContractorQuota[]>(
        "/inspections/contractor-quotas/",
        { org_uuids: sorted },
      );
      return res.data ?? [];
    },
    staleTime: 60_000, // 1 min - the matrix doesn't move fast
  });
}

// ── Eligibility pool + staging (Chunk C / Gap 5) ─────────────

/** One row in the eligibility pool - an eligible warranty registration. */
export interface PoolRegistration {
  holding_uuid: string;
  registration_reference: string;
  registration_number: string;
  status: string;
  customer_name: string;
  customer_email: string;
  product_name: string;
  property_address: {
    line_1: string;
    line_2: string;
    city: string;
    region: string;
    postal_code: string;
    country: string;
  };
  /** Coarse "vicinity" the home's postcode resolves to (Chunk C2). Null when
   * the postcode is not in the property-ZIP-to-zone map or there is no host. */
  property_zone_code: string | null;
  property_zone_name: string | null;
  attributes: Record<string, unknown>;
  already_staged: boolean;
  has_paid_warranty: boolean;
  open_claim_count: number;
  activated_at: string | null;
  created_at: string;
}

export interface PoolParams {
  /** Installing-contractor uuid - the normal contractor-scoped flow. */
  contractor?: string;
  /** Sub-type the pool will stage into; drives the already_staged flag. */
  sub_type?: string;
  status?: string;
  search?: string;
  activated_after?: string;
  activated_before?: string;
  /** `'true'` to drop registrations with an open claim. */
  exclude_with_open_claims?: string;
  /** `'true'` / `'false'` to filter on a paid warranty. */
  has_paid_warranty?: string;
  /** Re-inspection cadence: keep only homes due again (last completed
   * inspection older than this many months, or never inspected). */
  cycle_months?: number;
  /** Generic EAV attribute filters, `{code_name: [values]}`. Serialised as
   * `attr.<code_name>=v1,v2`; values within a code OR, codes AND. */
  attributeFilters?: Record<string, string[]>;
  /** `'true'` to include registrations already in the pipeline. */
  include_staged?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface PaginatedPool {
  count: number;
  next: string | null;
  previous: string | null;
  results: PoolRegistration[];
}

/**
 * Eligibility pool feed - paginated, contractor-scoped warranty
 * registrations eligible to be staged for inspection. Backed by the
 * StandardListAPIView, so the response is the DRF-native
 * `{count,next,previous,results}` shape (the envelope interceptor leaves
 * it intact). Pass `enabled=false` to defer until a contractor is picked.
 */
export function usePoolRegistrations(params: PoolParams = {}, enabled = true) {
  const { attributeFilters, ...scalar } = params;
  const sp = new URLSearchParams(
    Object.entries(scalar)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => [k, String(v)]),
  );
  // Flatten the EAV attribute filters into `attr.<code_name>=v1,v2` params.
  for (const [code, values] of Object.entries(attributeFilters ?? {})) {
    const clean = (values ?? []).filter(Boolean);
    if (clean.length) sp.set(`attr.${code}`, clean.join(","));
  }
  const search = sp.toString();
  const qs = search ? `?${search}` : "";
  return useQuery({
    queryKey: ["inspection-pool", params],
    enabled,
    queryFn: async () => {
      const res = await api.get<PaginatedPool>(`/inspections/pool/${qs}`);
      return res.data;
    },
  });
}

// ── Pool filter options (Chunk C2) ───────────────────────────
//
// The holding-level SELECT / MULTI_SELECT registration attributes the pool
// can be filtered on (roof pitch, number of stories, ...). Served from the
// API so the Pool tab renders one dropdown per filterable attribute with no
// hardcoded option lists.

export interface PoolFilterOption {
  value: string;
  label: string;
}

export interface PoolFilterAttribute {
  code_name: string;
  label: string;
  field_type: string;
  options: PoolFilterOption[];
}

export function useInspectionPoolFilterOptions() {
  return useQuery({
    queryKey: ["inspection-pool-filter-options"],
    queryFn: async () => {
      const res = await api.get<PoolFilterAttribute[]>(
        "/inspections/pool/filter-options/",
      );
      return res.data ?? [];
    },
    staleTime: 1000 * 60 * 30,
  });
}

/** Per-registration outcome of a stage request. */
export interface StageOutcome {
  holding_uuid: string;
  status: string;
  inspection_code: string;
  inspection_uuid: string;
  reason: string;
}

export interface StageResult {
  summary: { staged: number; skipped: number; errors: number; total: number };
  staged: StageOutcome[];
  skipped: StageOutcome[];
  errors: StageOutcome[];
}

/**
 * Stage one or more registrations from the pool into the staged queue.
 * Each becomes a DRAFT inspection; the existing `schedule` action then
 * puts it on the calendar. Invalidates the pool, the list/staged feeds,
 * and the contractor-quota badges.
 */
export function useStageRegistrations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      holding_uuids: string[];
      sub_type_key: string;
      contractor_uuid?: string;
      notes?: string;
    }) => {
      const res = await api.post<StageResult>(
        "/inspections/pool/stage/",
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection-pool"] });
      qc.invalidateQueries({ queryKey: ["inspections"] });
      qc.invalidateQueries({ queryKey: ["inspection-contractor-quotas"] });
    },
  });
}

// ── Proximity-aware bulk staging (Chunk C2) ──────────────────

/** Shared eligibility filters for the auto-stage actions, mirroring the
 * pool query so a batch is drawn from exactly what the coordinator sees. */
export interface AutoStageFilters {
  attribute_filters?: Record<string, string[]>;
  cycle_months?: number | null;
  exclude_with_open_claims?: boolean;
  has_paid_warranty?: boolean | null;
}

export interface AutoStageContractorPayload extends AutoStageFilters {
  mode?: "contractor";
  sub_type_key: string;
  contractor_uuid: string;
  /** Number of nearest eligible homes to stage. */
  limit: number;
}

export interface AutoStageCyclePayload extends AutoStageFilters {
  mode: "cycle";
  sub_type_key: string;
  /** Optional cap on how many to stage per behind-schedule contractor. */
  max_per_contractor?: number;
}

export type AutoStagePayload =
  AutoStageContractorPayload | AutoStageCyclePayload;

export interface CycleContractorOutcome {
  contractor_uuid: string;
  contractor_name: string;
  staged: number;
  skipped: number;
  errors: number;
}

export interface AutoStageResult {
  mode: "contractor" | "cycle";
  summary: { staged: number; skipped: number; errors: number; total: number };
  staged?: StageOutcome[];
  skipped?: StageOutcome[];
  errors?: StageOutcome[];
  by_contractor?: CycleContractorOutcome[];
}

/**
 * Proximity-aware bulk staging. `mode: 'contractor'` stages the closest N
 * eligible homes for one contractor; `mode: 'cycle'` auto-generates a cycle
 * list across every behind-schedule contractor. Both reuse the standard
 * staging path, so they invalidate the same feeds as `useStageRegistrations`.
 */
export function useAutoStageRegistrations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AutoStagePayload) => {
      const res = await api.post<AutoStageResult>(
        "/inspections/pool/auto-stage/",
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection-pool"] });
      qc.invalidateQueries({ queryKey: ["inspections"] });
      qc.invalidateQueries({ queryKey: ["inspection-contractor-quotas"] });
    },
  });
}

// ── Notes timeline ───────────────────────────────────────

/**
 * Inspection note kinds. Mirrors `InspectionNoteKind` on the backend
 * - keep the literal values in sync with
 * `backend/apps/inspections/models.py`.
 */
export type InspectionNoteKind =
  | "general"
  | "customer_confirmation"
  | "contractor_confirmation"
  | "scheduling"
  | "follow_up";

export type InspectionNoteVisibility =
  "staff_only" | "shared_with_customer" | "shared_with_contractor";

export interface InspectionNote {
  uuid: string;
  title: string;
  kind: InspectionNoteKind;
  kind_label: string;
  visibility: InspectionNoteVisibility;
  visibility_label: string;
  shared_with_customer: boolean;
  shared_with_contractor: boolean;
  visibility_summary: string;
  body: string;
  created_by_email: string;
  created_by_name: string;
  created_at: string;
}

export function useInspectionNotes(uuid: string | undefined) {
  return useQuery({
    queryKey: ["inspection-notes", uuid],
    enabled: !!uuid,
    queryFn: async () => {
      const res = await api.get<InspectionNote[]>(
        `/inspections/${uuid}/notes/`,
      );
      return res.data ?? [];
    },
  });
}

export function useCreateInspectionNote(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      title?: string;
      body: string;
      kind?: InspectionNoteKind;
      shared_with_customer?: boolean;
      shared_with_contractor?: boolean;
    }) => {
      const res = await api.post<InspectionNote>(
        `/inspections/${uuid}/notes/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection-notes", uuid] });
    },
  });
}

export interface InspectionEvidenceUploadPayload {
  file_uuid: string;
  kind?: string;
  caption?: string;
  captured_at?: string | null;
  gps_lat?: number | null;
  gps_lng?: number | null;
  tagged_line_items?: string[];
}

export function useUploadInspectionEvidence(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: InspectionEvidenceUploadPayload) => {
      const res = await api.post<InspectionEvidence>(
        `/inspections/${uuid}/evidence/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inspection", uuid] });
    },
  });
}

// ---------------------------------------------------------------------------
// Smart Schedule itineraries
// ---------------------------------------------------------------------------

export type ItineraryStatus = "draft" | "scheduled" | "cancelled";

export interface ItinerarySummary {
  uuid: string;
  name: string;
  region_label: string;
  status: ItineraryStatus;
  stop_count: number;
  day_count: number;
  target_stops: number;
  per_stop_minutes: number;
  max_day_minutes: number;
  origin_address: string;
  total_distance_meters: number | null;
  total_duration_seconds: number | null;
  start_date: string | null;
  inspector_uuid: string | null;
  inspector_name: string;
  created_at: string;
}

export interface ItineraryStopRow {
  inspection_uuid: string;
  code: string;
  stop_index: number;
  address: string;
  lat: number | null;
  lng: number | null;
  contractor_name: string;
  sub_type_label: string;
  lifecycle_status: string;
  trigger_source: string;
}

export interface ItineraryDay {
  day_index: number;
  date: string | null;
  drive_seconds_estimate: number | null;
  /** One contractor's block was too big for a day and runs on into this one. */
  spans_group?: boolean;
  stops: ItineraryStopRow[];
}

/** A geocode-resolved start/end point (canonical address + coordinates). */
export interface ResolvedPoint {
  lat: number;
  lng: number;
  formatted_address: string;
}

export interface ItineraryDetail extends ItinerarySummary {
  days: ItineraryDay[];
  origin_point: ResolvedPoint | null;
  end_point: ResolvedPoint | null;
  end_address: string;
  /** Working-day start (HH:MM) chosen at generation; '' = default 09:00. */
  day_start_time: string;
}

export interface GenerateItinerariesPayload {
  sub_type_key: string;
  /**
   * Additional inspection sub-types to stage in the same run. The backend
   * accepts the scalar `sub_type_key` on its own for back-compat; these are
   * merged with it, deduped, and share ONE quota budget per contractor.
   */
  sub_type_keys?: string[];
  /**
   * Route priority work (claim-triggered, manual, API-created and
   * hand-marked) alongside the pool instead of pool-only.
   */
  include_priority?: boolean;
  contractor_uuids?: string[];
  /**
   * Geographic scope. The four axes cascade - AND between axes, OR within
   * one - and every value is matched case-insensitively. `zones` carries
   * GeoZone codes (plant service areas), resolved server-side through the
   * tenant's seeded postal-code map.
   */
  states?: string[];
  cities?: string[];
  countries?: string[];
  zones?: string[];
  target_stops?: number;
  per_stop_minutes?: number;
  max_day_minutes?: number;
  origin_address?: string;
  end_address?: string;
  /** Working-day start (HH:MM); slots space out from here on assign. */
  day_start_time?: string;
  max_trips?: number;
  stage_first?: boolean;
  attribute_filters?: Record<string, string[]>;
  cycle_months?: number | null;
  exclude_with_open_claims?: boolean;
  has_paid_warranty?: boolean | null;
}

export interface GenerateItinerariesResult {
  summary: {
    itineraries: number;
    stops: number;
    skipped: number;
    leftover_count: number;
    staged_created: number;
  };
  itineraries: ItinerarySummary[];
  skipped: { uuid: string; reason: string }[];
  leftover_count: number;
  staged_created: number;
}

export interface AssignItineraryResult {
  scheduled: string[];
  skipped: { uuid: string; reason: string }[];
  days: { date: string; inspection_uuids: string[] }[];
  warnings: string[];
  status: ItineraryStatus;
}

export interface ItinerarySuggestion {
  inspection_uuid: string;
  code: string;
  address: string;
  contractor_name: string;
  sub_type_label: string;
  trigger_source: string;
  contractor_priority: string;
  property_state: string;
  /** False = a different state than the trip (offered, clearly labeled). */
  in_trip_state: boolean;
  /**
   * Straight-line km to the closest stop already on this day, and which stop
   * that is. `null` when it could not be measured - render nothing rather
   * than "0 km", which would read as "right next door".
   */
  distance_km: number | null;
  nearest_stop_index: number | null;
}

export function useItineraries(status?: ItineraryStatus | "all") {
  return useQuery({
    queryKey: ["inspection-itineraries", status ?? "all"],
    queryFn: async () => {
      const params = new URLSearchParams({ page_size: "200" });
      if (status && status !== "all") params.set("status", status);
      const res = await api.get<{ results: ItinerarySummary[]; count: number }>(
        `/inspections/itineraries/?${params.toString()}`,
      );
      return res.data;
    },
  });
}

export function useItinerary(uuid: string | undefined) {
  return useQuery({
    queryKey: ["inspection-itinerary", uuid],
    enabled: !!uuid,
    queryFn: async () => {
      const res = await api.get<ItineraryDetail>(
        `/inspections/itineraries/${uuid}/`,
      );
      return res.data;
    },
  });
}

function invalidateItineraries(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["inspection-itineraries"] });
  qc.invalidateQueries({ queryKey: ["inspection-itinerary"] });
  qc.invalidateQueries({ queryKey: ["inspections"] });
  qc.invalidateQueries({ queryKey: ["contractor-summary"] });
  qc.invalidateQueries({ queryKey: ["contractor-quotas"] });
}

export function useGenerateItineraries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: GenerateItinerariesPayload) => {
      const res = await api.post<GenerateItinerariesResult>(
        "/inspections/itineraries/generate/",
        payload,
      );
      return res.data;
    },
    onSuccess: () => invalidateItineraries(qc),
  });
}

export function useAssignItinerary(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      inspector_uuid: string;
      start_date: string;
    }) => {
      const res = await api.post<AssignItineraryResult>(
        `/inspections/itineraries/${uuid}/assign/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => invalidateItineraries(qc),
  });
}

export interface ReleaseItineraryResult {
  released: string[];
  skipped: { uuid: string; reason: string }[];
  status: ItineraryStatus;
}

export interface ReassignItineraryResult {
  rescheduled: string[];
  skipped: { uuid: string; reason: string }[];
  warnings: string[];
  status: ItineraryStatus;
}

/**
 * Hand a scheduled trip back to the pool. The trip returns to DRAFT with
 * its stops intact, so it can be edited and re-assigned rather than
 * cancelled and regenerated. Stops already past scheduling come back in
 * `skipped` - they stay booked.
 */
export function useReleaseItinerary(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { reason?: string }) => {
      const res = await api.post<ReleaseItineraryResult>(
        `/inspections/itineraries/${uuid}/release/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => invalidateItineraries(qc),
  });
}

/** Move a scheduled trip to a different field tech, dates, or both. */
export function useReassignItinerary(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      inspector_uuid?: string;
      start_date?: string;
      reason?: string;
      /**
       * Whether each moved stop sends its scheduled letter. Omitted or true
       * keeps the long-standing behaviour; false mutes this move only.
       */
      notify?: boolean;
    }) => {
      const res = await api.post<ReassignItineraryResult>(
        `/inspections/itineraries/${uuid}/reassign/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => invalidateItineraries(qc),
  });
}

export function useCancelItinerary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (uuid: string) => {
      const res = await api.post<{ status: ItineraryStatus }>(
        `/inspections/itineraries/${uuid}/cancel/`,
        {},
      );
      return res.data;
    },
    onSuccess: () => invalidateItineraries(qc),
  });
}

export function useAddItineraryStop(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      inspection_uuid: string;
      day_index: number;
    }) => {
      const res = await api.post<ItineraryDetail>(
        `/inspections/itineraries/${uuid}/stops/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => invalidateItineraries(qc),
  });
}

export function useRemoveItineraryStop(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inspectionUuid: string) => {
      const res = await api.delete<ItineraryDetail>(
        `/inspections/itineraries/${uuid}/stops/${inspectionUuid}/`,
      );
      return res.data;
    },
    onSuccess: () => invalidateItineraries(qc),
  });
}

export function useReoptimizeItineraryDay(uuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dayIndex: number) => {
      const res = await api.post<ItineraryDetail>(
        `/inspections/itineraries/${uuid}/reoptimize-day/`,
        { day_index: dayIndex },
      );
      return res.data;
    },
    onSuccess: () => invalidateItineraries(qc),
  });
}

export function useItinerarySuggestions(
  uuid: string | undefined,
  dayIndex: number,
  enabled: boolean,
  search = "",
) {
  return useQuery({
    queryKey: ["inspection-itinerary-suggestions", uuid, dayIndex, search],
    enabled: !!uuid && enabled,
    queryFn: async () => {
      const params = new URLSearchParams({ day_index: String(dayIndex) });
      if (search.trim()) params.set("search", search.trim());
      const res = await api.get<{ results: ItinerarySuggestion[] }>(
        `/inspections/itineraries/${uuid}/suggestions/?${params.toString()}`,
      );
      return res.data.results;
    },
  });
}

// ---------------------------------------------------------------------------
// Contractor community summary (Pool table)
// ---------------------------------------------------------------------------

export interface ContractorSummaryRow {
  organisation_uuid: string;
  /** Deep link target: /network/{partnership_uuid}. Empty when unknown. */
  partnership_uuid: string;
  name: string;
  status: string;
  priority: string;
  tier_label: string;
  target_pct: string;
  registrations: number;
  due: number;
  carryover: number;
  done: number;
  remaining: number;
  behind_schedule: boolean;
  scheduled: number;
  staged: number;
  issues: { failed: number; open_punch_items: number };
}

export interface ContractorSummaryGroup {
  group: ContractorSummaryRow;
  children: ContractorSummaryRow[];
}

/** A zone facet needs the code to filter on AND a name to show. */
export interface ZoneFacet {
  zone: string;
  label: string;
  count: number;
}

export interface ContractorSummary {
  year: number;
  results: ContractorSummaryGroup[];
  /** Staged-and-unrouted candidates per axis (Smart Schedule scope chips). */
  candidates_by_state: { state: string; count: number }[];
  candidates_by_city?: { city: string; count: number }[];
  candidates_by_country?: { country: string; count: number }[];
  candidates_by_zone?: ZoneFacet[];
  /** Prior-year paid registrations per axis (the Pool toolbar filters). */
  registration_states: { state: string; count: number }[];
  registration_cities?: { city: string; count: number }[];
  registration_countries?: { country: string; count: number }[];
  registration_zones?: ZoneFacet[];
}

export interface ContractorSummaryParams {
  year?: number;
  /** Scope every count to one property state (e.g. 'OH'). */
  state?: string;
  /** Scope every count to one property city / country / GeoZone code. */
  city?: string;
  country?: string;
  zone?: string;
  /** Override the matrix percentage: due = ceil(pct/100 x registrations). */
  targetPct?: string;
}

export function useContractorSummary(params: ContractorSummaryParams = {}) {
  const { year, state, city, country, zone, targetPct } = params;
  return useQuery({
    queryKey: [
      "contractor-summary",
      year ?? "current",
      state ?? "",
      city ?? "",
      country ?? "",
      zone ?? "",
      targetPct ?? "",
    ],
    queryFn: async () => {
      const search = new URLSearchParams();
      if (year) search.set("year", String(year));
      if (state) search.set("state", state);
      if (city) search.set("city", city);
      if (country) search.set("country", country);
      if (zone) search.set("zone", zone);
      if (targetPct) search.set("target_pct", targetPct);
      const qs = search.toString();
      const res = await api.get<ContractorSummary>(
        `/inspections/pool/contractors/${qs ? `?${qs}` : ""}`,
      );
      return res.data;
    },
  });
}
